import { KButton, KSpan, KTitleSpan } from "kahuna-base-react-components"
import React, { useEffect, useState } from "react"
import { connect } from "react-redux"
import { Dialog } from "@headlessui/react"
import { useNavigate } from "react-router-dom"

import { paymentMethods } from "../../../apis"
import { USER_ROLE } from "../../../constants"
import { lang } from "../../../constants/languages"
import { RootState } from "../../../store"
import { User } from "../../../types"

const PAYMENT_METHODS_PATH = "/account/payment-methods"
// The accounts that are paid out: the artist and the collaborator holding shares of a release.
const REMINDED_ROLES = [USER_ROLE.Artist, USER_ROLE.Collaborator]

// Remembered per account, so the reminder is said once and another account on the same browser still
// gets its own.
const seenKey = (userId: number) => `paymentMethodReminderSeen_${userId}`

const wasSeen = (userId: number) => {
  try {
    return localStorage.getItem(seenKey(userId)) === "1"
  } catch {
    return true
  }
}

const markSeen = (userId: number) => {
  try {
    localStorage.setItem(seenKey(userId), "1")
  } catch {
    // Without storage the reminder may come back on the next visit, which is the lesser harm.
  }
}

/**
 * Tells an artist or collaborator who has no payment method yet to add one, once, when the app opens:
 * without it the royalties they earn cannot be paid out. Comes after the announcements and before the
 * unread notifications modal, and calls onDone when it has nothing more to show, so the two never stack.
 */
const PaymentMethodReminderModal = ({ user, onDone }: { user: User; onDone: () => void }) => {
  const [open, setOpen] = useState(false)
  const navigate = useNavigate()

  useEffect(() => {
    const applies =
      !!user?.id &&
      REMINDED_ROLES.includes(user.roleId as USER_ROLE) &&
      !wasSeen(user.id) &&
      !window.location.pathname.startsWith(PAYMENT_METHODS_PATH)
    if (!applies) return onDone()

    paymentMethods("/", "get")
      .then((response) => {
        if ((response.data.results || []).length) return onDone()
        markSeen(user.id!)
        setOpen(true)
      })
      .catch(() => onDone())
  }, [user?.id])

  const close = () => {
    setOpen(false)
    onDone()
  }

  return (
    <Dialog open={open} onClose={close}>
      <div
        className="fixed w-screen h-screen top-0 left-0 flex items-center justify-center bg-[#0000004d]"
        style={{ zIndex: 9999 }}
        onClick={close}
      >
        <div
          className="p-6 shadow-md rounded-[10px] bg-[#fff] flex flex-col items-start justify-center"
          style={{ maxWidth: 440 }}
          onClick={(event) => event.stopPropagation()}
        >
          <KTitleSpan text={lang.payment_method_reminder.title} lineHeight="22px" fontSize={20} />
          <div className="mt-5">
            <KSpan text={lang.payment_method_reminder.text} lineHeight="20px" />
          </div>
          <div className="flex w-full mt-8 items-center justify-end gap-2">
            <KButton
              dataTestId="payment-method-reminder-later"
              width="auto"
              background="#F7F7F7"
              textColor="#111"
              text={lang.payment_method_reminder.later}
              onClick={close}
            />
            <KButton
              dataTestId="payment-method-reminder-add"
              width="auto"
              background="black"
              textColor="white"
              text={lang.payment_method_reminder.add}
              onClick={() => {
                close()
                navigate(PAYMENT_METHODS_PATH)
              }}
            />
          </div>
        </div>
      </div>
    </Dialog>
  )
}

const mapStateToProps = (state: RootState) => ({ user: state.auth.user })

export default connect(mapStateToProps)(PaymentMethodReminderModal)
